import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { IconButton, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { Archive, Trash01 } from '../../../../assets';
import { SetStateAction, Dispatch, useState } from 'react';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { ConfirmationDeleteDialog } from '../../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { AutoshipTemplateModel, OrderType } from '../../../../app/services/api/orderManagementClient';
import { PaginatedList } from '../../../../app/services/api/orderManagementClient';
import { Add } from '@mui/icons-material';
import { AutoshipTemplateStatusEnum } from '../../enums/AutoshipmentTemplateStatusEnum';
import { Pill } from '../../../../components/shared/Pill';
import { formatDateHourString, formatDateTime } from '../../../../utils';
import { useNavigate } from 'react-router-dom';
import { AutoshipmentTemplateTabEnum } from '../../enums/AutoshipmentTemplateTabEnum';

export default function AutoshipmentTemplateTable(props: {
  readonly list: PaginatedList<AutoshipTemplateModel>,
  readonly activeTab: AutoshipmentTemplateTabEnum,
  readonly page: number,
  readonly setPage: Dispatch<SetStateAction<number>>,
  readonly order: 'asc' | 'desc',
  readonly setOrder: Dispatch<SetStateAction<'asc' | 'desc'>>,
  readonly orderBy: string,
  readonly setOrderBy: Dispatch<SetStateAction<string>>,
  readonly clearAll: () => void;
  readonly handleDelete: (id: number) => Promise<void>;
  readonly handleArchive: (id: number) => Promise<void>;
  readonly isLoading: boolean;
  readonly navigateToTemplate: (id: number) => void;
  readonly orderTypes: OrderType[];
  readonly hasWritePermissions: boolean;
  readonly hasDeletePermissions: boolean;
}) {
  const { t } = useTranslation('autoshipment');
  const navigate = useNavigate();
  const theme = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<number | null>(null);

  const rowsPerPage = 10;
  const maxPages = props.list?.totalCount
    ? Math.max(1, Math.ceil(props.list.totalCount / rowsPerPage))
    : 0;

  const deleteButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    setDeleteTemplateId(id);
    setOpenModal(true);
  };

  const handleConfirmDeleteModal = async () => {
    if (deleteTemplateId !== null) {
      await props.handleDelete(deleteTemplateId);
      setOpenModal(false);
      setDeleteTemplateId(null);
    }
  };

  const handleCancelModal = () => {
    setOpenModal(false);
    setDeleteTemplateId(null);
  };

  const archiveButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    props.handleArchive(id);
  };

  if (props.list.items?.length == 0) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('title')}
      onClear={props.clearAll}
      onAdd={() => navigate('/manage-autoshipments/template')}
      addItemText={t('newTemplateBtn')}
      onAddIsCreating={false} />;
  }

  const handleSchedulesClick = (event: React.MouseEvent, templateId: number) => {
    event.stopPropagation();
    navigate(`/manage-autoshipments/schedules/${templateId}`);
  };

  return (
    <>
      <TableHeader>
        {t('templatesTitle')}
        {props.list?.totalCount && <Pill text={`${props.list?.totalCount} Template${props.list?.totalCount === 1 ? '' : 's'}`}></Pill>}
      </TableHeader>
      {props.isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={props.activeTab === AutoshipmentTemplateTabEnum.ARCHIVED ? 700 : 480} /> :
        <TableContainer>
          <Table sx={{ minWidth: props.activeTab === AutoshipmentTemplateTabEnum.ARCHIVED ? '700px' : '480px' }}>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('templateName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'orderTypeId'}
                    direction={props.order}
                  >
                    {t('orderType')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'itemCount'}
                    direction={props.order}>
                    {t('itemCount')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'scheduleCount'}
                    direction={props.order}
                  >
                    {t('schedulesCreated')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'reason'}
                    direction={props.order}
                  >
                    {t('reason')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={props.orderBy === 'createDate'}
                    direction={props.order}
                  >
                    {t('created')}
                  </TableSortLabel>
                </TableCell>
                {props.activeTab === AutoshipmentTemplateTabEnum.ACTIVE && props.hasWritePermissions && (
                  <TableCell></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list?.items.map((row: AutoshipTemplateModel) => (
                <TableRow key={row.id}
                  onClick={() => props.navigateToTemplate(row.id!)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.name ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {
                        props.orderTypes.find(x => x.id === row.orderTypeId)?.name
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.itemCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='textXS'
                      fontWeight={700}
                      color={theme.palette.primary[500]}
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={(event) => handleSchedulesClick(event, row.id ?? -1)}
                    >
                      {row.scheduleCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.reasons}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.createDate ? formatDateTime(row.createDate!) : ''}
                    </Typography>
                  </TableCell>
                  {props.activeTab === AutoshipmentTemplateTabEnum.ACTIVE && props.hasWritePermissions &&
                    <TableCell
                      align='left'
                      sx={{ p: 0, alignContent: 'center' }}
                    >
                      <IconButton aria-label={t('archive')}
                        size='small'
                        onClick={(event) => archiveButtonHandler(event, row.id ?? -1)}>
                        <SvgIcon style={{ color: theme.palette.info[500] }}><Archive></Archive></SvgIcon>
                      </IconButton>
                      {row.scheduleCount === 0 && props.hasDeletePermissions &&
                        <IconButton aria-label={t('delete')}
                          size='small'
                          onClick={(event) => deleteButtonHandler(event, row.id ?? -1)}>
                          <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                        </IconButton>
                      }
                    </TableCell>
                  }
                </TableRow>
              ))}

              <EmptyRows numberOfCurrentRows={props.list.items?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={props.isLoading} />
      <ConfirmationDeleteDialog open={openModal}
        onCancel={handleCancelModal}
        onConfirm={handleConfirmDeleteModal}
        nameOfObject={t('templateLabel')} />
    </>
  );
}